import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import {
  addKMSCluster,
  addKMSEnclave,
  addKMSIdentity,
  addKMSKey,
  addKMSPolicy,
  deleteKMSCluster,
  deleteKMSEnclave,
  deleteKMSIdentity,
  deleteKMSInstance,
  deleteKMSKey,
  deleteKMSPolicy,
  fetchKMSClusterDetails,
  fetchKMSClusters,
  fetchKMSDetails,
  fetchKMSEnclaveDetails,
  fetchKMSEnclaves,
  fetchKMSIdentities,
  fetchKMSIdentityDetails,
  fetchKMSKeyDetails,
  fetchKMSKeys,
  fetchKMSPolicies,
  fetchKMSPolicyDetails,
  fetchKMSSupportedAPIs,
} from "./kmsThunks";
import { KmsAPI } from "api/kmsApi";
import {
  MinioEnclave,
  MinioKeyManagerDetails,
  MinioKMSIdentity,
  MinioKMSKey,
  MinioKMSPolicyResponse,
  MinioKMSPolicyStatus,
} from "../../api/keymanageRpcApi";
import { LoadingStatus } from "../../common/types";

export interface EncryptionState {
  supportedAPIs: KmsAPI[];
  loadingAPIs: boolean;

  currentEnclave: MinioEnclave | null;
  enclaves: MinioEnclave[];
  enclaveDetails: MinioEnclave | null;
  loadingEnclaves: LoadingStatus;

  policies: MinioKMSPolicyStatus[];
  policyDetails: MinioKMSPolicyResponse;
  loadingPolicies: LoadingStatus;

  keys: MinioKMSKey[];
  keyDetails: MinioKMSKey | null;
  loadingKeys: boolean;

  clusters: any[];
  clusterDetails: any;
  loadingClusters: boolean;

  loadingAdd: boolean;
  loadingDelete: boolean;
  loadingDetails: boolean;

  identities: MinioKMSIdentity[];
  identityDetails: MinioKMSIdentity | null;
  loadingIdentities: boolean;

  details: MinioKeyManagerDetails | null;
  loadingDetailsPage: boolean;
  loadingDeleteInstance: boolean;
  deleteInstanceModalOpen: boolean;
  kmsDeleted: boolean;
}

const initialState: EncryptionState = {
  supportedAPIs: [],
  loadingAPIs: false,

  currentEnclave: null,
  enclaves: [],
  enclaveDetails: {},
  loadingEnclaves: "neverLoaded",

  policies: [],
  policyDetails: {},
  loadingPolicies: "idle",

  keys: [],
  keyDetails: {},
  loadingKeys: false,

  identities: [],
  identityDetails: {},
  loadingIdentities: false,

  clusters: [],
  clusterDetails: {},
  loadingClusters: false,

  loadingAdd: false,
  loadingDelete: false,
  loadingDetails: false,

  details: null,
  loadingDetailsPage: true,
  loadingDeleteInstance: false,
  deleteInstanceModalOpen: false,
  kmsDeleted: false,
};

export const kmsSlice = createSlice({
  name: "kms",
  initialState,
  reducers: {
    setEnclave: (state, action: PayloadAction<MinioEnclave>) => {
      state.currentEnclave = action.payload;
    },
    setLoadingPolicies: (state, action: PayloadAction<LoadingStatus>) => {
      state.loadingPolicies = action.payload;
    },
    setLoadingEnclaves: (state, action: PayloadAction<LoadingStatus>) => {
      state.loadingEnclaves = action.payload;
    },
    setLoadingKeys: (state, action: PayloadAction<boolean>) => {
      state.loadingKeys = action.payload;
    },
    setLoadingIdentities: (state, action: PayloadAction<boolean>) => {
      state.loadingIdentities = action.payload;
    },
    setLoadingClusters: (state, action: PayloadAction<boolean>) => {
      state.loadingClusters = action.payload;
    },
    setLoadingAdd: (state, action: PayloadAction<boolean>) => {
      state.loadingAdd = action.payload;
    },
    setLoadingDelete: (state, action: PayloadAction<boolean>) => {
      state.loadingDelete = action.payload;
    },
    setLoadingDetails: (state, action: PayloadAction<boolean>) => {
      state.loadingDetails = action.payload;
    },
    setLoadingDetailsPage: (state, action: PayloadAction<boolean>) => {
      state.loadingDetailsPage = action.payload;
    },
    setDeleteInstanceOpen: (state, action: PayloadAction<boolean>) => {
      state.deleteInstanceModalOpen = action.payload;
    },
    setKMSDeleted: (state, action: PayloadAction<boolean>) => {
      state.kmsDeleted = action.payload;
    },
    setLoadingKMS: (state, action: PayloadAction<boolean>) => {
      state.loadingDetailsPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ********** KMS Supported APIs *********
      .addCase(fetchKMSSupportedAPIs.fulfilled, (state, action) => {
        state.loadingAPIs = false;
        state.supportedAPIs = action.payload;
      })
      .addCase(fetchKMSSupportedAPIs.pending, (state) => {
        state.loadingAPIs = true;
      })
      .addCase(fetchKMSSupportedAPIs.rejected, (state, action) => {
        state.loadingAPIs = false;
        state.supportedAPIs = [];
      })
      // ********** KMS Policies *********
      .addCase(fetchKMSPolicies.fulfilled, (state, action) => {
        state.loadingPolicies = "idle";
        state.policies = action.payload.items!;
      })
      .addCase(fetchKMSPolicies.pending, (state) => {
        state.loadingPolicies = "loading";
      })
      .addCase(fetchKMSPolicies.rejected, (state, action) => {
        state.loadingPolicies = "failed";
        state.policies = [];
      })
      .addCase(fetchKMSPolicyDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.policyDetails = action.payload;
      })
      .addCase(fetchKMSPolicyDetails.pending, (state) => {
        state.loadingDetails = true;
      })
      .addCase(fetchKMSPolicyDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.policyDetails = {};
      })
      .addCase(addKMSPolicy.fulfilled, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(addKMSPolicy.pending, (state) => {
        state.loadingAdd = true;
      })
      .addCase(addKMSPolicy.rejected, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(deleteKMSPolicy.fulfilled, (state, action) => {
        state.loadingDelete = false;
      })
      .addCase(deleteKMSPolicy.pending, (state) => {
        state.loadingDelete = true;
      })
      .addCase(deleteKMSPolicy.rejected, (state, action) => {
        state.loadingDelete = false;
      })
      // ********** KMS Enclaves *********
      .addCase(fetchKMSEnclaves.fulfilled, (state, action) => {
        state.loadingEnclaves = "idle";
        if (action.payload) {
          state.enclaves = action.payload.items!;
          if ((action.payload?.items ?? []).length > 0) {
            state.currentEnclave = action.payload?.items?.[0]!;
          }
        }
      })
      .addCase(fetchKMSEnclaves.pending, (state) => {
        state.loadingEnclaves = "loading";
      })
      .addCase(fetchKMSEnclaves.rejected, (state, action) => {
        state.loadingEnclaves = "failed";
        state.enclaves = [];
      })
      .addCase(fetchKMSEnclaveDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.enclaveDetails = action.payload;
      })
      .addCase(fetchKMSEnclaveDetails.pending, (state) => {
        state.loadingDetails = true;
      })
      .addCase(fetchKMSEnclaveDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.enclaveDetails = {};
      })
      .addCase(addKMSEnclave.fulfilled, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(addKMSEnclave.pending, (state) => {
        state.loadingAdd = true;
      })
      .addCase(addKMSEnclave.rejected, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(deleteKMSEnclave.fulfilled, (state, action) => {
        state.loadingDelete = false;
      })
      .addCase(deleteKMSEnclave.pending, (state) => {
        state.loadingDelete = true;
      })
      .addCase(deleteKMSEnclave.rejected, (state, action) => {
        state.loadingDelete = false;
      })
      // ********** KMS Keys *********
      .addCase(fetchKMSKeys.fulfilled, (state, action) => {
        state.loadingKeys = false;
        state.keys = action.payload!;
      })
      .addCase(fetchKMSKeys.pending, (state) => {
        state.loadingKeys = true;
      })
      .addCase(fetchKMSKeys.rejected, (state, action) => {
        state.loadingKeys = false;
        state.keys = [];
      })
      .addCase(fetchKMSKeyDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.keyDetails = action.payload;
      })
      .addCase(fetchKMSKeyDetails.pending, (state) => {
        state.loadingDetails = true;
      })
      .addCase(fetchKMSKeyDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.keyDetails = {};
      })
      .addCase(addKMSKey.fulfilled, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(addKMSKey.pending, (state) => {
        state.loadingAdd = true;
      })
      .addCase(addKMSKey.rejected, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(deleteKMSKey.fulfilled, (state, action) => {
        state.loadingDelete = false;
      })
      .addCase(deleteKMSKey.pending, (state) => {
        state.loadingDelete = true;
      })
      .addCase(deleteKMSKey.rejected, (state, action) => {
        state.loadingDelete = false;
      })
      // ********** KMS Identities *********
      .addCase(fetchKMSIdentities.fulfilled, (state, action) => {
        state.loadingIdentities = false;
        state.identities = action.payload!;
      })
      .addCase(fetchKMSIdentities.pending, (state) => {
        state.loadingIdentities = true;
      })
      .addCase(fetchKMSIdentities.rejected, (state, action) => {
        state.loadingIdentities = false;
        state.identities = [];
      })
      .addCase(fetchKMSIdentityDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.identityDetails = action.payload;
      })
      .addCase(fetchKMSIdentityDetails.pending, (state) => {
        state.loadingDetails = true;
      })
      .addCase(fetchKMSIdentityDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.identityDetails = {};
      })
      .addCase(addKMSIdentity.fulfilled, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(addKMSIdentity.pending, (state) => {
        state.loadingAdd = true;
      })
      .addCase(addKMSIdentity.rejected, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(deleteKMSIdentity.fulfilled, (state, action) => {
        state.loadingDelete = false;
      })
      .addCase(deleteKMSIdentity.pending, (state) => {
        state.loadingDelete = true;
      })
      .addCase(deleteKMSIdentity.rejected, (state, action) => {
        state.loadingDelete = false;
      })
      // ********** KMS Cluesters *********
      .addCase(fetchKMSClusters.fulfilled, (state, action) => {
        state.loadingClusters = false;
        state.clusters = action.payload;
      })
      .addCase(fetchKMSClusters.pending, (state) => {
        state.loadingClusters = true;
      })
      .addCase(fetchKMSClusters.rejected, (state, action) => {
        state.loadingClusters = false;
        state.keys = [];
      })
      .addCase(fetchKMSClusterDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.clusterDetails = action.payload;
      })
      .addCase(fetchKMSClusterDetails.pending, (state) => {
        state.loadingDetails = true;
      })
      .addCase(fetchKMSClusterDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.clusterDetails = {};
      })
      .addCase(addKMSCluster.fulfilled, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(addKMSCluster.pending, (state) => {
        state.loadingAdd = true;
      })
      .addCase(addKMSCluster.rejected, (state, action) => {
        state.loadingAdd = false;
      })
      .addCase(deleteKMSCluster.fulfilled, (state, action) => {
        state.loadingDelete = false;
      })
      .addCase(deleteKMSCluster.pending, (state) => {
        state.loadingDelete = true;
      })
      .addCase(deleteKMSCluster.rejected, (state, action) => {
        state.loadingDelete = false;
      })
      // KMS Details
      .addCase(fetchKMSDetails.pending, (state) => {
        state.loadingDetailsPage = true;
      })
      .addCase(fetchKMSDetails.fulfilled, (state, action) => {
        state.loadingDetailsPage = false;
        state.details = action.payload;
      })
      .addCase(fetchKMSDetails.rejected, (state, action) => {
        state.loadingDetailsPage = false;
      })
      // KMS Delete Instance
      .addCase(deleteKMSInstance.pending, (state) => {
        state.loadingDeleteInstance = true;
      })
      .addCase(deleteKMSInstance.fulfilled, (state, action) => {
        state.loadingDeleteInstance = false;
        state.kmsDeleted = true;
      })
      .addCase(deleteKMSInstance.rejected, (state, action) => {
        state.loadingDeleteInstance = false;
      });
  },
});

// Reducer actions
export const {
  setEnclave,
  setLoadingPolicies,
  setLoadingEnclaves,
  setLoadingKeys,
  setLoadingIdentities,
  setLoadingClusters,
  setLoadingAdd,
  setLoadingDelete,
  setLoadingDetails,
  setLoadingDetailsPage,
  setDeleteInstanceOpen,
  setKMSDeleted,
  setLoadingKMS,
} = kmsSlice.actions;

// Reducer selectors
export const currentEnclave = (state: RootState) => state.kms.currentEnclave;
export const enclaves = (state: RootState) => state.kms.enclaves;
export const enclaveDetails = (state: RootState) => state.kms.enclaveDetails;
export const loadingEnclaves = (state: RootState) => state.kms.loadingEnclaves;

export const policies = (state: RootState) => state.kms.policies;
export const loadingPolicies = (state: RootState) => state.kms.loadingPolicies;

export const keys = (state: RootState) => state.kms.keys;
export const keyDetails = (state: RootState) => state.kms.keyDetails;
export const loadingKeys = (state: RootState) => state.kms.loadingKeys;

export const identities = (state: RootState) => state.kms.identities;
export const identityDetails = (state: RootState) => state.kms.identityDetails;
export const loadingIdentities = (state: RootState) =>
  state.kms.loadingIdentities;

export const clusters = (state: RootState) => state.kms.clusters;
export const loadingClusters = (state: RootState) => state.kms.loadingClusters;

export const loadingAdd = (state: RootState) => state.kms.loadingAdd;
export const loadingDelete = (state: RootState) => state.kms.loadingDelete;
export const loadingDetails = (state: RootState) => state.kms.loadingDetails;

export default kmsSlice.reducer;
